import { ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DisplayedColumnsChangedEvent, RowNode, SelectionChangedEvent } from 'ag-grid-community';
import * as lo_ from 'lodash';
import { Subscription } from 'rxjs';
import * as _ from 'underscore';
import { isArray, isBoolean, isNullOrUndefined, isNumber, isString, isUndefined } from '../../models/utils';
import { Comparison } from '../../models/comparison';
import { ContextMenuOption } from '../../models/context-menu-option';
import { DocumentFormat, DocumentViewerContext, DrDocument, MainEmailThreadDocument } from '../../models/dr-document';
import { DrRequestInfo } from '../../models/dr-request-info';
import {
    DocumentListTabType, Endpoint, EntityType, HelpIndex, MessageType, ModalTargetType,
    RemoveRestoreDocsOperationType, SaveToRemoveFromOperationType, TagOperationType, UpdateStatusType, SearchType
} from '../../models/enums';
import { CellActionType, GenericColDef, GridContext, GridRowSelectionType } from '../../models/grid-context';
import { NavigationContentComponent } from '../../models/navigation-content-component';
import { ObjectActionListType, Permissions } from '../../models/permission';
import { SearchResult } from '../../models/search-result';
import { ViewType } from '../../models/view';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { GenericGridComponent } from '../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../generic-tree/generic-tree-node';
import { AddToRemoveFromModalComponent, AddToRemoveFromModalData } from '../modals/add-to-remove-from-modal/add-to-remove-from-modal.component';
import { CreateManifestModalComponent } from '../modals/create-manifest/create-manifest.component';
import { OcrSettingsComponent, OCRSettingsModalData } from '../modals/ocr-settings/ocr-settings.component';
import { RemoveRestoreDocsModalComponent, RemoveRestoreDocsModalData } from '../modals/remove-restore-docs-modal/remove-restore-docs-modal.component';
import { ReprocessModalComponent, ReprocessModalData } from '../modals/reprocess-modal/reprocess-modal-modal.component';
import { ExtractOffice365DataModalComponent, ExtractOffice365DataModalData } from '../modals/extract-office-365-data-modal/extract-office-365-data-modal.component';
import { TagsModalComponent, TagsModalData } from '../modals/tags-modal/tags-modal.component';
import { TargetPickerModalComponent } from '../modals/target-picker-modal/target-picker-modal.component';
import { UpdateMetadataModalComponent } from '../modals/update-metadata-modal/update-metadata-modal.component';
import { TargetPickerTreeType } from '../target-picker-tree/target-picker-tree.component';
import { DocumentListData, DocumentListInfo } from './document-list-info/document-list-info';
import { DocumentListColumnType, allColumnDefs, ReorderedDocumentListColumnInfo, DocumentListColumnInfo } from './document-list-info/document-list-column-info';
import { TaskType } from '../../models/task';
import { WorkflowStep } from '../../models/workflow';
import { DynamicFolder } from '../../models/dynamic-folder';
import { ContextMenuComponent } from '../context-menu/context-menu.component';
import { DownloadAsPDFStampModalComponent } from '../modals/download-as-pdf-stamp-modal/download-as-pdf-stamp-modal.component';
import { DiscardPileLogComponent } from '../modals/discard-pile-log-modal/discard-pile-log-modal.component';

@Component({
    standalone: false,
    selector: 'res-document-list',
    templateUrl: './document-list.component.html',
    styleUrls: ['./document-list.component.scss']
})
export class DocumentListComponent implements NavigationContentComponent, OnDestroy {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent) genericGrid: GenericGridComponent;
    @ViewChild('headerTemplate', { static: true }) headerTemplate: TemplateRef<any>;
    @ViewChild('documentHeaderContextMenu') documentHeaderContextMenu: ContextMenuComponent;

    selectedNode: GenericTreeNode;
    get selectedNodeData(): any {
        if (this.selectedNode.data instanceof Comparison) {
            return (this.selectedNode.data as Comparison).currentComparisonView;
        }
        return this.selectedNode.data;
    }

    documentListData: DocumentListData;
    generateButtonVisible: boolean;
    hideDuplicatesVisible: boolean;
    hideDuplicates = false;
    hideDuplicatesLabel = 'Hide Duplicates';
    dupeCount = 0;
    docCountWithDuplicates = 0;

    gridName: string;
    automationId: string;
    helpIndex: HelpIndex;
    updateStatusType: UpdateStatusType;
    columnDefs: Array<GenericColDef>;

    rowSelection: GridRowSelectionType;
    requestInfo: DrRequestInfo;
    gridContext: GridContext;

    hasHeaderTemplate: boolean;
    pagerVisible: boolean;
    documentCount: number;
    foundDocumentCount: number;
    searchSize: number;
    isBusy = false;
    isBusyMessage: string;
    currentColumns: any;
    entityType: EntityType;

    contextMenuOptions: Array<ContextMenuOption>;
    documentHeaderButtonOptions: Array<ContextMenuOption>;

    @Input() onSelectionChanged: (event?: SelectionChangedEvent) => void;
    @Input() loadDataFinishedCallback: () => void;
    @Input() isBusyOnInit = false;

    isDocumentViewerDocList: boolean;
    isDocumentViewerFamilyList: boolean;
    isDocumentViewerThreadList: boolean;
    isMainEmailThreads: boolean;
    isShortMessageThreads: boolean;
    isDocumentViewerExceptionList: boolean;
    hasComparisionPermissions: boolean;
    isGeneratedComGridsDocList: boolean;
    findMoreEvent: EventEmitter<any> = new EventEmitter();
    currentEntityType: EntityType;
    private entityChangedEvent: Subscription;
    isMatrixGridSelection: boolean;
    isMatrixTab: boolean;
    totalItemsCount: number

    @Input() gridPagerCustomCallback: (startIndex) => void;

    @Input() gridPagerAfterLoadCustomCallback = () => {
        const gridElement = document.getElementsByClassName("ag-body-viewport");
        if (gridElement && gridElement[0]) {
            gridElement[0].scrollTo(0, 0);
        }
    };

    @Input() responseArrayCallback: (value: any, fullResponse: any) => void;
    @Input() responseCallback = (response: any) => {
        if (response) {
            if (!response.documents || !isArray(response.documents)) {
                response.documents = [];
            }
            let documents = (<Array<any>>response.documents).map(d => new DrDocument(d));
            if (this.requestInfo.data.groupFamilies || response.attachments) {
                const groupedDocs = new Array<DrDocument>();
                documents.forEach(document => {
                    if (document.family_handle && document.isAttachment) {
                        // family child, look for parent
                        for (let i = documents.length - 1; i >= 0; i--) {
                            if (documents[i].family_handle === document.family_handle && !documents[i].isAttachment) {
                                document.isChild = true;
                                document.level = 1;
                                if (!documents[i].isParent) {
                                    documents[i].isParent = true;
                                    documents[i].children = new Array<DrDocument>();
                                    documents[i].isExpanded = true;
                                }
                                documents[i].children.push(document);
                                break;
                            }
                        }
                    }
                    if (!document.isChild) {
                        groupedDocs.push(document);
                    }
                });
                documents = groupedDocs;
            }

            response.documents = documents;
            if (response.view) {
                if (this.hideDuplicates) {
                    response.totalCount = response.view.deDupeDocumentCount;
                    this.generateButtonVisible = response.documents.length === 0 && response.view.documentCount > 0;
                } else {
                    response.totalCount = response.view.documentCount;
                    this.generateButtonVisible = response.documents.length === 0 && response.totalCount > 0;
                }
                this.foundDocumentCount = response.view.documentCount;
                this.docCountWithDuplicates = response.view.documentCount;
                const hideDupsCount = this.currentEntityType === EntityType.clusterView || this.currentEntityType === EntityType.exportVolume;
                this.dupeCount = response.view.deDupeDocumentCount ? response.view.documentCount - response.view.deDupeDocumentCount : 0;
                this.hideDuplicatesLabel = this.dupeCount === 0 || hideDupsCount ? `Hide Duplicates` : `Hide ${this.dupeCount.toLocaleString()} Duplicate${this.dupeCount === 1 ? '' : 's'}`;
                const searchSizeObj = response.view.attributes ? _.find(response.view.attributes, attr => attr.name === 'targetsize') : null;
                if (searchSizeObj) {
                    this.searchSize = searchSizeObj.value;
                }
            } else {
                response.totalCount = response.numberResults || response.documents.length;
                this.generateButtonVisible = response.documents.length === 0 && response.totalCount > 0;
                this.hideDuplicatesLabel = `Hide Duplicates`;
            }
            this.documentCount = response.totalCount;

            if (this.isMatrixTab) {
                this.totalItemsCount = response.totalCount;
                this.isMatrixGridSelection = false;
            }

            this.loadCurrentColumns();
        }
    }
    @Input() cellClickedCallback = (rowNode: RowNode, action: CellActionType) => {
        if (rowNode && rowNode.data instanceof DrDocument && action) {
            if (action === CellActionType.openFamily) {
                this.openDocumentViewer(rowNode.data, DocumentListTabType.family, false);
            } else if (action === CellActionType.openThread) {
                this.openDocumentViewer(rowNode.data, DocumentListTabType.thread, false);
            }
        }
    }
    cellDoubleClickedCallback = (rowNode: RowNode) => {
        if (rowNode && rowNode.data instanceof DrDocument) {
            this.openDocumentViewer(rowNode.data, (this.isMainEmailThreads || this.isShortMessageThreads) ? DocumentListTabType.thread : DocumentListTabType.docList, false);
        }
    }

    constructor(private commonService: CommonService,
        private restService: RestService,
        private changeDetector: ChangeDetectorRef,
        private router: Router) {
        this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
            const entityType = this.commonService.updateStatusTypeToEntityType(data);
            if ((entityType === this.currentEntityType || (data === UpdateStatusType.WORKING_SET && this.currentEntityType === EntityType.discardPile)) && this.requestInfo?.data?.viewHandle && !data?.isDeleteOperation) {
                this.isBusy = true;
                this.restService.post(Endpoint.viewManager_getView, { handle: this.requestInfo.data.viewHandle, selectedAttributes: [] }).then(responseGetView => {
                    this.requestInfo.data.skipCalculation = responseGetView.view && (responseGetView.view.documentCount > 100000 ||
                        (responseGetView.view.documentCount === 0 && responseGetView.view.busy));
                    if (data === UpdateStatusType.RESULT_VIEW) {
                        this.requestInfo.data.startIndex = null;
                    }
                    this.loadData();
                }).catch(error => this.isBusy = false);
            }
        });
    }

    selectedTreeItemChanged = (node: GenericTreeNode, hideCheckBoxColumn?: boolean) => {
        this.isMatrixTab = this.commonService.selectedNavigationNode?.navigationInfo?.selectedTab ? (this.commonService.selectedNavigationNode.navigationInfo?.selectedTab?.name === 'Communication Grid'
            || this.commonService.selectedNavigationNode.navigationInfo?.selectedTab?.name === 'Domain Grid') : (this.commonService.matrixGridsSelection?.selectedTab?.name === 'Communication Grid' ||
                this.commonService.matrixGridsSelection?.selectedTab?.name === 'Domain Grid');
        if (isNullOrUndefined(this.commonService.selectedNavigationNode?.navigationInfo?.selectedTab) && this.isMatrixTab) {
            this.commonService.selectedNavigationNode.navigationInfo.selectedTab = this.commonService.matrixGridsSelection?.selectedTab;
        }
        this.hasComparisionPermissions = Permissions.checkUserPermission(ObjectActionListType.DOCUMENT_REPORTS);
        this.documentListData = null;
        this.hideDuplicates = false;
        this.docCountWithDuplicates = 0;
        this.hideDuplicatesLabel = 'Hide Duplicates';
        if (node && node.data && node.entityType && DocumentListInfo.has(node.entityType)) {
            let nodeEntityType: EntityType;
            if (node.entityType === EntityType.searchResult && node.data.attributes?.find(a => a.name === 'targetViewName' && a.value?.includes('Discard Pile'))) {
                nodeEntityType = EntityType.searchResultDiscardPile;
            } else if (node.data.task === SearchType.VECTOR_SET_FIND_DUPLICATES) {
                nodeEntityType = EntityType.exactOrContentDuplicatesSearchResult;
            } else if (node.data.viewType === ViewType.CORPUS_RESULT || node.data.viewType === ViewType.CORPUS_SET_RESULT) {
                nodeEntityType = EntityType.searchResultImport;
            } else if (node.data.viewType === ViewType.WORKING_SET_RESULT && (node.data.task === SearchType.COLLECTION_SIMILARITY || node.data.taskType === TaskType.NEAR_DUPE
                || node.data.taskType === TaskType.SEARCH_BY_PROFILE)) {
                nodeEntityType = EntityType.similaritySearchResult;
            } else if (node.entityType === EntityType.searchResult && node.data.attributes?.find(a => a.name === 'sampleSetResult')) {
                nodeEntityType = EntityType.sampleSet;
            } else if (node.entityType === EntityType.searchResult && node.data.attributes?.find(a => a.name === 'exportErrorsMetaDataBulkSearch')) {
                nodeEntityType = EntityType.searchResultExportErrors;
            } else if (node.navigationInfo?.selectedTab?.path === 'import-exceptions') {
                nodeEntityType = EntityType.exceptionView;
            } else {
                nodeEntityType = node.entityType;
            }
            if (DocumentListInfo.has(nodeEntityType)) {
                this.entityType = nodeEntityType;
                this.documentListData = DocumentListInfo.get(nodeEntityType)(this, node);
                let entityTypeWithEmailThreads = this.isMainEmailThreads ? EntityType.emailThread : (this.isShortMessageThreads ? EntityType.shortMessageThread : this.entityType);
                if (this.commonService.documentViewerOpenContext?.isShortMessageThreads || (this.isDocumentViewerDocList && entityTypeWithEmailThreads === EntityType.shortMessageThread)) {
                    entityTypeWithEmailThreads = EntityType.shortMessageThreadDocList;
                }
                const isNotThreadEntityType = entityTypeWithEmailThreads !== EntityType.emailThread && entityTypeWithEmailThreads !== EntityType.shortMessageThread && entityTypeWithEmailThreads !== EntityType.shortMessageThreadDocList;
                const reorderedEntityTypeWithEmailThreadsCols = ReorderedDocumentListColumnInfo.get(entityTypeWithEmailThreads);
                if ((reorderedEntityTypeWithEmailThreadsCols && isNotThreadEntityType) || entityTypeWithEmailThreads === EntityType.clusterView) {
                    if (this.entityType === EntityType.clusterView || this.entityType === EntityType.projectData || this.entityType === EntityType.comparison || this.entityType === EntityType.tagView || this.entityType === EntityType.folder || this.entityType === EntityType.custodian || this.entityType === EntityType.mediaID || this.entityType === EntityType.batch) {
                        this.documentListData.columnDefs = ReorderedDocumentListColumnInfo.get(EntityType.projectData);
                    } else if (this.entityType === EntityType.export || this.entityType === EntityType.exportVolume) {
                        this.documentListData.columnDefs = ReorderedDocumentListColumnInfo.get(EntityType.export);
                    } else {
                        this.documentListData.columnDefs = ReorderedDocumentListColumnInfo.get(entityTypeWithEmailThreads);
                    }
                } else if (reorderedEntityTypeWithEmailThreadsCols) {
                    this.documentListData.columnDefs = reorderedEntityTypeWithEmailThreadsCols;
                }
                if (this.commonService.isExternalApp && this.entityType === EntityType.clusterView) {
                    this.documentListData.columnDefs = DocumentListColumnInfo.get(EntityType.projectData);
                }
                this.documentListData.columnDefs.forEach(col => {
                    if (!col.automationId || this.isDocumentViewerDocList || this.isDocumentViewerFamilyList || this.isDocumentViewerThreadList) { col.automationId = this.documentListData.automationId; }
                    if (this.documentListData?.gridName === "Email Threads") {
                        col.automationId = col.automationId.replace('document-viewer', this.entityType === 'projectData' ? 'project-data' : this.entityType);
                    }
                    else {
                        col.automationId = col.automationId.replace('family', 'documents');
                        col.automationId = col.automationId.replace('threads', 'documents');
                    }
                });
                this.hideDuplicates = this.commonService.suppressDuplicates;
                let documentViewerContext: DocumentViewerContext;
                const checkBoxColumn = allColumnDefs.get(DocumentListColumnType.checkbox)(false);
                if (this.isDocumentViewerDocList || hideCheckBoxColumn) {
                    // update ReorderedDocumentListColumnInfo's currentInfo type to remove checkbox column from the array
                    this.documentListData.columnDefs = this.documentListData.columnDefs.filter(col => col.colId !== 'checkbox');
                } else {
                    const checkboxColIndex = this.documentListData.columnDefs.map(function (e) { return e.colId; }).indexOf('checkbox');
                    if (checkboxColIndex === -1) {
                        this.documentListData.columnDefs.unshift(checkBoxColumn);
                    }
                    ReorderedDocumentListColumnInfo.set(entityTypeWithEmailThreads, this.documentListData.columnDefs);
                }
                if (this.isDocumentViewerDocList && this.commonService.documentViewerOpenContext) {
                    documentViewerContext = this.commonService.documentViewerOpenContext;
                } else if (!this.isDocumentViewerDocList && this.commonService.documentViewerCloseContext) {
                    documentViewerContext = this.commonService.documentViewerCloseContext;
                    this.commonService.documentViewerCloseContext = null;
                }
                const isThreadMode = this.isMainEmailThreads || this.isShortMessageThreads;
                if (documentViewerContext) {
                    if (isNumber(documentViewerContext.startIndex) && !isThreadMode) {
                        this.documentListData.requestInfo.data.startIndex = documentViewerContext.startIndex;
                    }
                    if (isString(documentViewerContext.sortBy)) {
                        this.documentListData.gridContext.sortBy = documentViewerContext.sortBy;
                    }
                    if (isBoolean(documentViewerContext.sortAscending)) {
                        this.documentListData.gridContext.sortAscending = documentViewerContext.sortAscending;
                    }
                    if (isBoolean(documentViewerContext.hideDuplicates)) {
                        this.hideDuplicates = documentViewerContext.hideDuplicates;
                    }
                    if (documentViewerContext?.isGeneratedComGridsDocList) {
                        this.documentListData.requestInfo.data.viewHandle = this.commonService?.documentViewerOpenContext?.selectedDocument?.handle?.split('@')[0];
                        if (this.commonService.matrixGridsSelection) {
                            this.commonService.matrixGridsSelection.isDocViewerOpened = true;
                        }
                    }
                }
                if (!this.isDocumentViewerFamilyList && !this.isDocumentViewerThreadList && !isThreadMode && !this.isDocumentViewerExceptionList) {
                    this.documentListData.requestInfo.data.suppressDuplicates = this.hideDuplicates;
                }
                Object.assign(this, this.documentListData);
                if (this.commonService.isExternalApp) {
                    this.columnDefs.forEach(colDef => colDef.automationId = 'document-viewer-documents');
                }
                if (this.documentListData.isValid) {
                    if (this.currentEntityType && this.currentEntityType === nodeEntityType) {
                        // same item type, do not reload grid itself, just refresh data with new request data
                        if (this.genericGrid) {
                            this.genericGrid.allRowsSelected = false;
                        }
                    } else {
                        this.selectedNode = null;
                        if (!this.changeDetector['destroyed']) {
                            this.changeDetector.detectChanges();
                        }
                    }
                    this.currentEntityType = nodeEntityType;
                    this.selectedNode = node;
                    this.loadData();
                }
            }
        }
    }

    disableContextMenuOptionsByParsingVersion(item: any, projectParsingVersion: string): void {
        item.enabled = projectParsingVersion === 'V2';
        item.tooltipFunction = () => projectParsingVersion === 'V1' ? 'You can no longer perform this operation for a Project using Parsing Library Version V1.' : '';
        item.tooltip = projectParsingVersion === 'V1' ? 'You can no longer perform this operation for a Project using Parsing Library Version V1.' : '';
        item.tooltipAlwaysOn = projectParsingVersion === 'V1';
    }

    loadData(): void {
        if (this.documentListData.isValid) {
            if (!this.changeDetector['destroyed']) {
                this.changeDetector.detectChanges();
            }
            if (this.genericGrid) {
                this.genericGrid.requestInfo = this.requestInfo;
                this.genericGrid.helpIndex = this.isMatrixTab ? null : this.helpIndex;
                this.genericGrid.loadData();
            }
        }
    }

    selectedItems(): string {
        if (this.genericGrid) {
            const count = this.genericGrid.selectedRows.length;
            if (count > 0) {
                if (this.genericGrid.allRowsSelected) {
                    if (this.currentEntityType === 'exportVolume' || this.currentEntityType === 'clusterView') {
                        return `All`;
                    } else {
                        return `All ${this.hideDuplicates || this.docCountWithDuplicates > 0 ? this.docCountWithDuplicates.toLocaleString() : this.genericGrid.totalItems.toLocaleString()}`;
                    }
                }
                return this.isMatrixTab ? (`${count.toLocaleString()}`) : `${count.toLocaleString()} on this page`;
            }
            return `${count.toLocaleString()}`;
        }
        return '0';
    }

    hiddenDuplicatesPresent(): string {
        if (this.genericGrid && this.genericGrid.allRowsSelected && this.hideDuplicates && !(this.currentEntityType === 'exportVolume' || this.currentEntityType === 'clusterView')) {
            return this.dupeCount === 0 ? '' : `(including ${this.dupeCount.toLocaleString()} hidden doc${this.dupeCount === 1 ? '' : 's'})`;
        }
        return '';
    }

    onDocumentHeaderButton(event: MouseEvent, selectedNode: GenericTreeNode): void {
        let eventTarget = event.target as HTMLElement;
        eventTarget = eventTarget?.nodeName === 'I' || eventTarget?.nodeName === 'SPAN' ? eventTarget.parentElement : eventTarget;
        const parentElCoordinates = {
            x: eventTarget.getBoundingClientRect().left,
            y: eventTarget.getBoundingClientRect().top + eventTarget.getBoundingClientRect().height - 1
        }
        const node = {
            contextMenuOptions: this.documentHeaderButtonOptions,
            automationId: this.automationId,
            parentElCoordinates: parentElCoordinates
        }

        const projectParsingVersion = this.commonService?.currentProject?.attributes?.find(a => a.name === 'parsingVersion')?.value;
        const itemReprocessCM = node.contextMenuOptions?.find(o => o.name === 'Reprocess ...');
        const itemLoadFromExternalCM = node.contextMenuOptions?.find(o => o.name === 'Load from External Area ...');
        const itemCreateDsFromSearchResultCM = node.contextMenuOptions?.find(o => o.name === 'Create Data Set from Search Results ...');
        if (itemReprocessCM && ((selectedNode?.data as SearchResult)?.viewType === ViewType.CORPUS_SET_RESULT || (selectedNode?.data as SearchResult)?.viewType === ViewType.CORPUS_RESULT ||
            (selectedNode?.data as SearchResult)?.viewType === ViewType.WORKING_SET_RESULT) && projectParsingVersion === 'V1') {
            this.disableContextMenuOptionsByParsingVersion(itemReprocessCM, projectParsingVersion);
        } else if (itemReprocessCM) {
            itemReprocessCM.enabled = (selectedNode?.data instanceof WorkflowStep) ? this.genericGrid?.selectedRows?.length > 0 : ((selectedNode?.data instanceof SearchResult) && this.genericGrid?.selectedRows?.length > 0 && (selectedNode?.data?.viewType != null || !isUndefined(_.findWhere((selectedNode?.data as SearchResult)?.attributes, { name: 'viewtype' }))));
            itemReprocessCM.tooltipAlwaysOn = false;
            itemReprocessCM.tooltipFunction = (item: GenericTreeNode) => '';
        }
        if (itemLoadFromExternalCM && ((selectedNode?.data as SearchResult)?.viewType === ViewType.CORPUS_SET_RESULT || (selectedNode?.data as SearchResult)?.viewType === ViewType.CORPUS_RESULT) && projectParsingVersion === 'V1') {
            this.disableContextMenuOptionsByParsingVersion(itemLoadFromExternalCM, projectParsingVersion);
        } else if (itemLoadFromExternalCM) {
            itemLoadFromExternalCM.enabled = (selectedNode?.data instanceof SearchResult) && this.genericGrid?.selectedRows?.length > 0;
            itemLoadFromExternalCM.tooltipAlwaysOn = false;
            itemLoadFromExternalCM.tooltipFunction = (item: GenericTreeNode) => '';
        }
        if (itemCreateDsFromSearchResultCM && ((selectedNode?.data as SearchResult)?.viewType === ViewType.CORPUS_SET_RESULT || (selectedNode?.data as SearchResult)?.viewType === ViewType.CORPUS_RESULT) && projectParsingVersion === 'V1') {
            this.disableContextMenuOptionsByParsingVersion(itemCreateDsFromSearchResultCM, projectParsingVersion);
        } else if (itemCreateDsFromSearchResultCM) {
            itemCreateDsFromSearchResultCM.enabled = (selectedNode?.data instanceof SearchResult) && this.genericGrid?.selectedRows?.length > 0;
            itemCreateDsFromSearchResultCM.tooltipAlwaysOn = false;
            itemCreateDsFromSearchResultCM.tooltipFunction = (item: GenericTreeNode) => '';
        }
        this.changeDetector.detectChanges();

        this.genericGrid.onContextMenu(this.documentHeaderContextMenu, event, node, event.currentTarget);
    }

    onDisplayedColumnsChanged(event: DisplayedColumnsChangedEvent) {
        if (event?.api) {
            const newColStructure = <Array<any>>event.api.getAllGridColumns();
            const colDefArr: Array<GenericColDef> = [];
            newColStructure.forEach(col => {
                const isColVisible = col.visible;
                col.colId === 'context-menu' ? 'contextMenu' : col.colId;
                col.colId = col.colId === 'tagList' ? 'tags' : col.colId;
                col.colId = col.colId === 'displayName' ? 'filename' : col.colId;
                if (!isNaN(col.colId)) {
                    col.colId = col.colDef.cellTemplateType;
                }
                if (col.colId && allColumnDefs) {
                    const colId = col.colId;
                    if (colId && Object.values(DocumentListColumnType).includes(colId)) {
                        colDefArr.push(allColumnDefs.get(DocumentListColumnType[colId])(!isColVisible));
                    }
                }
            });
            let entityType = this.isMainEmailThreads ? EntityType.emailThread : (this.isShortMessageThreads ? EntityType.shortMessageThread : this.entityType);
            if (this.commonService.documentViewerOpenContext?.isShortMessageThreads) {
                entityType = EntityType.shortMessageThreadDocList;
            } else {
                if (entityType === EntityType.clusterView || entityType === EntityType.projectData || entityType === EntityType.comparison || entityType === EntityType.tagView || entityType === EntityType.folder || entityType === EntityType.custodian || entityType === EntityType.mediaID || entityType === EntityType.batch) {
                    ReorderedDocumentListColumnInfo.set(EntityType.projectData, colDefArr);
                } else if (entityType === EntityType.export || entityType === EntityType.exportVolume) {
                    ReorderedDocumentListColumnInfo.set(EntityType.export, colDefArr);
                } else {
                    ReorderedDocumentListColumnInfo.set(entityType, colDefArr);
                }
            }
        }
    }


    hideDuplicatesChanged(): void {
        this.requestInfo.data.suppressDuplicates = this.hideDuplicates;
        this.requestInfo.data.startIndex = 0;
        this.loadData();
    }

    generateDocumentList(): void {
        this.requestInfo.data.skipCalculation = false;
        this.loadData();
    }

    getPickerDisplayType(): UpdateStatusType {
        // If we're looking at a search result view, we need to determine if it was based on a project data view.  If not, we 
        // call it a Corpus (Import), but it might actually be Corpus Set (Data Area).  In our specific case - hiding picker nodes - it doesn't matter.
        return this.updateStatusType !== UpdateStatusType.RESULT_VIEW ? this.updateStatusType :
            this.selectedNode.data.viewType === ViewType.WORKING_SET_RESULT ? UpdateStatusType.WORKING_SET : UpdateStatusType.CORPUS;
    }

    onDocumentContextMenuOption(item: DrDocument, action: string): void {
        // handle context menu and context menu column option actions here
        switch (action) {
            case 'Open Document Inline': {
                this.openDocumentViewer(item, DocumentListTabType.docList, false);
                break;
            }
            case 'Open Document In New Window': {
                this.openDocumentViewer(item, DocumentListTabType.docList, true);
                break;
            }
            case 'Open Family Inline': {
                this.openDocumentViewer(item, DocumentListTabType.family, false);
                break;
            }
            case 'Open Family In New Window': {
                this.openDocumentViewer(item, DocumentListTabType.family, true);
                break;
            }
            case 'Open Thread Inline': {
                this.openDocumentViewer(item, DocumentListTabType.thread, false);
                break;
            }
            case 'Open Thread In New Window': {
                this.openDocumentViewer(item, DocumentListTabType.thread, true);
                break;
            }
            case 'Find Near Duplicates Of This ...':
            case 'Find Exact Duplicates Of This ...':
            case 'Find Content Duplicates Of This ...': {
                let dupType = 'NEAR';
                let modalDataType = TargetPickerTreeType.findNearDuplicates;
                if (action.includes('Exact')) {
                    dupType = 'EXACT';
                    modalDataType = TargetPickerTreeType.findExactDuplicates;
                } else if (action.includes('Content')) {
                    modalDataType = TargetPickerTreeType.findContentDuplicates;
                    dupType = 'CONTENT';
                }

                this.commonService.openModal(TargetPickerModalComponent, {
                    modalData: { targetPickerTreeType: modalDataType, updateStatusType: this.getPickerDisplayType() },
                    class: 'horizontal-px-700 vertical-percent-80'
                }).then(response => {
                    if (response && response.modalResult && response.modalData && response.modalData.target && response.modalData.target.data) {
                        this.isBusyMessage = `Finding ${dupType.toLowerCase()} duplicates ...`;
                        const target = response.modalData.target.data;
                        const handle = target.view ? target.view.handle : target.handle;
                        if (target instanceof WorkflowStep) {
                            response.modalData.target.data.name = response.modalData.target.data.description;
                        }

                        // TODO - remove isFindDuplicates after tree and selected item refresh will be refactored for search history items
                        this.commonService.isFindDuplicates = true;
                        this.restService.post(Endpoint.viewManager_searchForDuplicateDocuments, {
                            duplicateType: dupType,
                            sourceDocHandle: item.handle,
                            viewHandleList: [handle],
                            taskDescription: `Find ${dupType} duplicates for ` + item.displayName + ' in ' + target.name,
                            threshold: response.modalData.threshold
                        }).then(() => {
                            this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.RESULT_VIEW);
                            // TODO - remove isFindDuplicates after tree and selected item refresh will be refactored for search history items
                            // currently used to keep the busy spinner on if isFindDuplicates call is made until everything finishes
                            this.commonService.isFindDuplicates = false;
                        }, error => {
                            this.commonService.isFindDuplicates = false;
                        });
                        setTimeout(() => {
                            if (this.commonService.isFindDuplicates) {
                                this.genericGrid.setData([]);
                            }
                        }, 2000);
                    }
                });
                break;
            }
        }
    }

    isDocumentContextMenuOptionEnabled(item: DrDocument, action: string): boolean {
        if (action.includes('Near') && this.selectedNode.entityType === 'import') {
            return false;
        }
        return true;
    }

    isDocumentContextMenuOptionVisible(item: DrDocument | MainEmailThreadDocument, action: string): boolean {
        if (action.includes('Near') && (this.selectedNode.entityType === EntityType.import ||
            this.selectedNodeData.viewType === ViewType.CORPUS_RESULT || this.selectedNodeData.viewType === ViewType.CORPUS_SET_RESULT)) {
            return false;
        }
        if (action.includes('Inline') && this.commonService.isExternalApp) {
            return false;
        }
        if (action.includes('Family')) {
            return item instanceof DrDocument && item.hasFamily;
        }
        if (action.includes('Thread')) {
            return (item instanceof DrDocument && item.hasThread) || (item instanceof MainEmailThreadDocument && item.threadType && item.threadType.indexOf('THREAD') !== -1);
        }
        return true;
    }

    onDocumentHeaderButtonOption(action: string): void {
        const isThreadMode = this.isMainEmailThreads || this.isShortMessageThreads;
        const selectedTabPath = this.commonService.selectedNavigationNode?.navigationInfo?.selectedTab?.path
        const isAllRowsAndNotThreadTabPath = this.genericGrid.allRowsSelected && selectedTabPath !== 'email-thread' && selectedTabPath !== 'short-message-thread';
        // handle header button option actions here
        switch (action) {
            case 'Add Tags ...': {
                const targets = isAllRowsAndNotThreadTabPath ? [this.selectedNodeData] : this.genericGrid.selectedRows;
                let workflowStepType;
                if (targets[0] instanceof WorkflowStep && this.selectedNode.parent && this.selectedNode.parent.data) {
                    const attr = this.selectedNode.parent.data.queryItems.items[targets[0].orderOfExecution - 1].attributes.find(a => a.name === 'steptype');
                    workflowStepType = attr ? `Step ${targets[0].orderOfExecution}: ${attr.value}` : undefined;
                }
                this.commonService.openModal(TagsModalComponent, {
                    modalData: <TagsModalData>{
                        targets: targets,
                        targetType: this.genericGrid.allRowsSelected ? ModalTargetType.view : isThreadMode ? ModalTargetType.emailThread : ModalTargetType.document,
                        operationType: TagOperationType.tag,
                        updateStatusType: this.updateStatusType,
                        parentType: this.selectedNodeData instanceof SearchResult && (this.selectedNodeData.viewType === ViewType.CORPUS_RESULT
                            || this.selectedNodeData.viewType === ViewType.CORPUS_SET_RESULT) ? EntityType.import : this.selectedNode.entityType,
                        name: this.selectedNode.name,
                        workflowStepType: workflowStepType
                    }
                });
                break;
            }
            case 'Remove Tags ...': {
                const targets = isAllRowsAndNotThreadTabPath ? [this.selectedNodeData] : this.genericGrid.selectedRows;
                let workflowStepType;
                if (targets[0] instanceof WorkflowStep && this.selectedNode.parent && this.selectedNode.parent.data) {
                    const attr = this.selectedNode.parent.data.queryItems.items[targets[0].orderOfExecution - 1].attributes.find(a => a.name === 'steptype');
                    workflowStepType = attr ? `Step ${targets[0].orderOfExecution}: ${attr.value}` : undefined;
                }
                this.commonService.openModal(TagsModalComponent, {
                    modalData: <TagsModalData>{
                        targets: targets,
                        targetType: this.genericGrid.allRowsSelected ? ModalTargetType.view : isThreadMode ? ModalTargetType.emailThread : ModalTargetType.document,
                        operationType: TagOperationType.untag,
                        updateStatusType: this.updateStatusType,
                        parentType: this.selectedNodeData instanceof SearchResult && (this.selectedNodeData.viewType === ViewType.CORPUS_RESULT
                            || this.selectedNodeData.viewType === ViewType.CORPUS_SET_RESULT) ? EntityType.import : this.selectedNode.entityType,
                        name: this.selectedNode.name,
                        workflowStepType: workflowStepType
                    }
                });
                break;
            }
            case 'Add to ...': {
                const targets = this.genericGrid.allRowsSelected && !isThreadMode
                    ? [this.selectedNodeData] : this.genericGrid.selectedRows.map(doc => doc.handle);
                this.commonService.openModal(AddToRemoveFromModalComponent, {
                    modalData: <AddToRemoveFromModalData>{
                        targets: targets,
                        operationType: SaveToRemoveFromOperationType.add,
                        targetType: isThreadMode ? ModalTargetType.emailThread : this.genericGrid.allRowsSelected ? ModalTargetType.view : ModalTargetType.document,
                        nodeParent: this.selectedNodeData
                    }, class: 'horizontal-px-700'
                });
                break;
            }

            case 'Add to Project Data': {
                if (this.genericGrid.allRowsSelected) {
                    this.restService.post(Endpoint.viewManager_addFromView, {
                        filterSrcDuplicates: false,
                        includeFamilyMembers: true,
                        destViewHandle: this.commonService.projectData.handle,
                        srcViewHandle: this.commonService.matrixGridsSelection?.openFromDocumentActionBtn ? this.commonService.matrixGridsSelection?.viewHandle : this.selectedNodeData.handle,
                        taskDescription: 'Adding documents to Project Data'
                    });
                } else {
                    this.restService.post(Endpoint.viewManager_addFromDocuments, {
                        includeFamilyMembers: true,
                        destViewHandle: this.commonService.projectData.handle,
                        srcDocumentHandleList: this.genericGrid.selectedRows.map(doc => doc.handle),
                        taskDescription: 'Adding documents to Project Data'
                    });
                }
                break;
            }

            case 'Remove from ...': {
                const targets = this.genericGrid.allRowsSelected && !isThreadMode
                    ? [this.selectedNodeData] : this.genericGrid.selectedRows.map(doc => doc.handle);
                this.commonService.openModal(AddToRemoveFromModalComponent, {
                    modalData: <AddToRemoveFromModalData>{
                        targets: targets,
                        operationType: SaveToRemoveFromOperationType.remove,
                        targetType: isThreadMode ? ModalTargetType.emailThread : this.genericGrid.allRowsSelected ? ModalTargetType.view : ModalTargetType.document,
                    }, class: 'horizontal-px-700'
                });
                break;
            }
            case 'Remove from Project Data': {
                const targets = this.genericGrid.allRowsSelected ? [this.selectedNodeData] : this.genericGrid.selectedRows.map(doc => doc.handle);
                this.commonService.openModal(RemoveRestoreDocsModalComponent, {
                    modalData: <RemoveRestoreDocsModalData>{
                        targets: targets,
                        operationType: RemoveRestoreDocsOperationType.removeFromProjectData,
                        targetType: this.genericGrid.allRowsSelected ? ModalTargetType.view : ModalTargetType.document,
                        updateStatusType: UpdateStatusType.WORKING_SET
                    }
                });
                break;
            }
            case 'Send to Discard Pile ...': {
                const targets = this.genericGrid.allRowsSelected ? [this.selectedNodeData] : this.genericGrid.selectedRows.map(doc => doc.handle);
                this.commonService.openModal(RemoveRestoreDocsModalComponent, {
                    modalData: <RemoveRestoreDocsModalData>{
                        targets: targets,
                        operationType: RemoveRestoreDocsOperationType.sendToDiscardPile,
                        targetType: this.genericGrid.allRowsSelected ? ModalTargetType.view : ModalTargetType.document,
                        updateStatusType: UpdateStatusType.WORKING_SET
                    }
                });
                break;
            }
            case 'Restore from Discard Pile ...': {
                const targets = this.genericGrid.allRowsSelected ? [this.selectedNodeData] : this.genericGrid.selectedRows.map(doc => doc.handle);
                this.commonService.openModal(RemoveRestoreDocsModalComponent, {
                    modalData: <RemoveRestoreDocsModalData>{
                        targets: targets,
                        operationType: RemoveRestoreDocsOperationType.restoreFromDiscardPile,
                        targetType: this.genericGrid.allRowsSelected ? ModalTargetType.view : ModalTargetType.document,
                        updateStatusType: UpdateStatusType.WORKING_SET
                    }
                });
                break;
            }
            case 'Delete File(s) from Project': {
                const targets = this.genericGrid.allRowsSelected ? [this.selectedNodeData] : this.genericGrid.selectedRows.map(doc => doc.handle);
                this.commonService.openModal(RemoveRestoreDocsModalComponent, {
                    modalData: <RemoveRestoreDocsModalData>{
                        targets: targets,
                        operationType: RemoveRestoreDocsOperationType.deleteFilesFromProject,
                        targetType: this.genericGrid.allRowsSelected ? ModalTargetType.view : ModalTargetType.document,
                        updateStatusType: this.updateStatusType
                    }
                });
                break;
            }
            case 'Update Metadata ...': {
                const selectedDocHandles = this.genericGrid.allRowsSelected ?
                    (this.commonService.matrixGridsSelection?.openFromDocumentActionBtn ? this.commonService.matrixGridsSelection?.viewHandle : this.selectedNodeData.handle) : this.genericGrid.selectedRows.map(doc => doc.handle);
                this.commonService.openModal(UpdateMetadataModalComponent, { modalData: { docHandles: selectedDocHandles, selectedNode: this.selectedNode } });
                break;
            }
            case 'Reprocess ...': {
                const targets: Array<string> = this.genericGrid.allRowsSelected ?
                    (this.commonService.matrixGridsSelection?.openFromDocumentActionBtn ? [this.commonService.matrixGridsSelection?.viewHandle] : [this.selectedNodeData.handle]) : this.genericGrid.selectedRows.map(doc => doc.handle);
                this.commonService.openModal(ReprocessModalComponent, {
                    modalData: <ReprocessModalData>{
                        selectedNode: this.selectedNode,
                        targetType: this.genericGrid.allRowsSelected ? ModalTargetType.view : ModalTargetType.document,
                        targets: targets,
                        updateStatusType: this.updateStatusType
                    }
                });
                break;
            }
            case 'Extract Office 365 Data ...': {
                const targets: Array<string> = this.genericGrid.allRowsSelected ?
                    (this.commonService.matrixGridsSelection?.openFromDocumentActionBtn ? [this.commonService.matrixGridsSelection?.viewHandle] : [this.selectedNodeData.handle]) : this.genericGrid.selectedRows.map(doc => doc.handle);
                this.commonService.openModal(ExtractOffice365DataModalComponent, {
                    modalData: <ExtractOffice365DataModalData>{
                        targetType: this.genericGrid.allRowsSelected ? ModalTargetType.view : ModalTargetType.document,
                        targets: targets,
                        updateStatusType: this.updateStatusType,
                        auxParsingStatus: this.selectedNodeData.name
                    }
                });
                break;
            }
            case 'OCR ...': {
                if (this.selectedNodeData && this.genericGrid && this.genericGrid.selectedRows) {
                    const targets: Array<string> = this.genericGrid.allRowsSelected ? this.selectedNodeData : this.genericGrid.selectedRows.map(doc => doc.handle);
                    this.commonService.openModal(OcrSettingsComponent, {
                        modalData: <OCRSettingsModalData>{
                            targets: targets,
                            targetType: this.genericGrid.allRowsSelected ? ModalTargetType.view : ModalTargetType.document,
                            viewHandle: this.selectedNodeData.handle
                        },
                        class: 'horizontal-px-400'
                    });
                }
                break;
            }
            case 'Download as PDF': {
                this.commonService.openModal(DownloadAsPDFStampModalComponent, { class: 'horizontal-px-400' }).then(response => {
                    if (response?.modalResult && response.modalData) {
                        this.downloadNative(true, response.modalData.isStampEnabled);
                    }
                });
                break;
            }
            case 'Download Native': {
                this.downloadNative();
                break;
            }
            case 'Find More Like These ...': {
                const selected = this.genericGrid.selectedRows;
                if (selected && selected[0]) {
                    this.commonService.openModal(TargetPickerModalComponent, {
                        modalData: { targetPickerTreeType: TargetPickerTreeType.findMoreLikeThese, updateStatusType: this.getPickerDisplayType() },
                        class: 'horizontal-px-700 vertical-percent-80'
                    }).then(response => {
                        if (response && response.modalResult && response.modalData && response.modalData.target && response.modalData.threshold) {
                            const allRowsSelected = this.genericGrid.allRowsSelected;
                            const endpoint = allRowsSelected ? Endpoint.viewManager_searchByView : Endpoint.viewManager_searchByDocuments;
                            this.isBusy = true;
                            this.isBusyMessage = 'Finding more like these ...';
                            const target = response.modalData.target;
                            if (target.data instanceof WorkflowStep) {
                                response.modalData.target.name = response.modalData.target.data.description;
                            }
                            const allSelectedDocs = [];
                            if (selected.length > 0) {
                                selected.forEach(selectedDoc => {
                                    allSelectedDocs.push(selectedDoc.handle);
                                });
                            }
                            this.restService.post(endpoint, {
                                attributes: [{
                                    name: 'searchreporttype',
                                    value: ''
                                }],
                                documentHandles: allRowsSelected ? undefined : allSelectedDocs,
                                targetViewHandleList: [target.data instanceof DynamicFolder ? target.data.view?.handle : target.data.handle],
                                taskDescription: 'More like these search' + ' in ' + target.name,
                                sourceViewHandle: allRowsSelected ?
                                    (this.commonService.matrixGridsSelection?.openFromDocumentActionBtn &&
                                        (this.commonService.selectedNavigationNode?.navigationInfo?.selectedTab?.name === 'Communication Grid' ||
                                            this.commonService.selectedNavigationNode?.navigationInfo?.selectedTab?.name === 'Domain Grid')
                                    ) ? this.commonService.matrixGridsSelection?.viewHandle :
                                        (this.selectedNode.data instanceof DynamicFolder ? this.selectedNode.data.view?.handle : this.selectedNode.data.handle) : undefined,
                                threshold: response.modalData.threshold
                            }).then(() => {
                                this.isBusy = false;
                            }, error => {
                                this.isBusy = false;
                            });
                        }
                    });
                }
                break;
            }
            default: console.log('Unknown action attempted: ' + action);
        }
    }

    downloadNative(isPdfMode = false, isStampEnabled = false): void {
        if (this.genericGrid.allRowsSelected) {
            const docName1 = isPdfMode ? 'documents' : 'natives';
            const docName2 = isPdfMode ? 'PDFs' : 'natives';
            this.commonService.openConfirmationWarningModal(`You are attempting to download all ${docName1} in this list ${isPdfMode ? 'as PDFs' : ''}. Depending on the size of the documents, this could take considerable time and/or render the browser unresponsive. Consider creating a new export stream to produce the ${docName2} directly to an export location instead.`,
                'Warning', 'Continue', 'Cancel', null, 'custom-size-message').then(result => {
                    if (result.modalResult) {
                        let nodeViewHandle = this.selectedNodeData?.view ? this.selectedNodeData?.view.handle : this.selectedNodeData?.handle;
                        nodeViewHandle = this.commonService.matrixGridsSelection?.openFromDocumentActionBtn ? this.commonService.matrixGridsSelection?.viewHandle : nodeViewHandle;
                        if (nodeViewHandle) {
                            this.restService.post(Endpoint.documentManager_retrieveDocuments, {
                                viewHandle: nodeViewHandle,
                                documentInfos: null,
                                format: isPdfMode ? DocumentFormat.PDF : DocumentFormat.NATIVE,
                                applyStamp: isPdfMode ? isStampEnabled : undefined
                            });
                            this.showInformationDialog(isPdfMode);
                        }
                    }
                });
        } else {
            const selected = this.genericGrid.selectedRows;
            if (selected?.length > 0) {
                const documentInfos = _.filter(selected, (doc: DrDocument) => doc.filetype !== 'directory')
                    .map((thisDoc: DrDocument) => {
                        return {
                            name: '', handle:
                                thisDoc.handle.indexOf('@') === -1 ?
                                    (this.selectedNodeData as SearchResult).handle + '@' + thisDoc.handle : thisDoc.handle
                        };
                    });
                if (documentInfos?.length > 0) {
                    this.restService.post(Endpoint.documentManager_retrieveDocuments, {
                        documentInfos: documentInfos,
                        format: isPdfMode ? DocumentFormat.PDF : DocumentFormat.NATIVE,
                        applyStamp: isPdfMode ? isStampEnabled : undefined
                    });
                    const onlyOneDocSelected = selected.length === 1;
                    this.showInformationDialog(isPdfMode, onlyOneDocSelected);
                }
            }
        }
    }

    showInformationDialog(isPdfMode = false, singleDoc = false): void {
        let docText = singleDoc ? 'document is' : 'documents are';
        if (isPdfMode) {
            docText = docText.replace('document', 'PDF',);
        }
        this.commonService.openInformationToastNotification(`The ${docText} being prepared for downloading. Once finished, the archive (.zip) can be downloaded from the Work Basket.`);
    }

    isDocumentHeaderButtonOptionEnabled(action: string): boolean {
        // handle header button option enabled here
        // if an enabled flag is only tied to the specific document it was opened on, that can be done generally in the allDocumentListHeaderButtonOptions map
        switch (action) {
            case 'Add Tags ...': return true;
            case 'Remove Tags ...': return true;
            case 'Add to ...': return true;
            case 'Add to Project Data': return true;
            case 'Remove from ...': return true;
            case 'Remove from Project Data': return true;
            case 'Download as PDF': return true;
            case 'Download Native': return true;
            case 'Send to Discard Pile ...': return true;
            case 'Restore from Discard Pile ...': return true;
            case 'Delete File(s) from Project': {
                return this.genericGrid && this.genericGrid.selectedRows.filter(item => (item instanceof SearchResult) &&
                    (item as SearchResult).viewType === ViewType.WORKING_SET_RESULT).length === 0;
            }
            case 'Find More Like These ...': {
                return this.genericGrid && this.genericGrid.selectedRows.filter(item => (item instanceof SearchResult) &&
                    ((item as SearchResult).viewType === ViewType.CORPUS_RESULT ||
                        (item as SearchResult).viewType === ViewType.CORPUS_SET_RESULT)).length === 0;
            }
            case 'Update Metadata ...': {
                return this.genericGrid && this.genericGrid.selectedRows.filter(item => (item instanceof SearchResult) &&
                    (item as SearchResult).viewType === ViewType.WORKING_SET_RESULT).length === 0;
            }
            case 'Reprocess ...': return true;
            case 'Extract Office 365 Data ...': {
                if (this.commonService.mainContent?.office365Connectors) {
                    return this.commonService.mainContent.office365Connectors.length > 0;
                }
                return false;
            }
            case 'OCR ...': return true;

            default: {
                console.log('Unkwown action; disabling: ' + action);
                return false;
            }
        }
    }

    // handle header button option visibility here
    // if a visible flag is only tied to the specific document it was opened on, that can be done generally in the allDocumentListHeaderButtonOptions map
    isDocumentHeaderButtonOptionVisible(action: string): boolean {
        if (action === 'OCR ...' && this.selectedNode && this.selectedNodeData instanceof SearchResult &&
            ((this.selectedNodeData as SearchResult).viewType === ViewType.CORPUS_SET_RESULT || (this.selectedNodeData as SearchResult).viewType === ViewType.CORPUS_RESULT)
            && (this.selectedNodeData as SearchResult).attributes &&
            isNullOrUndefined((this.selectedNodeData as SearchResult).attributes.find(a => a.name === 'ocrView' || a.name === 'ocrCandidateDrillDown'))) {
            return false;
        }
        if ((action === 'Reprocess ...' || action === 'Send to Discard Pile ...') && (this.selectedNodeData as SearchResult).exportComparison) {
            return false;
        }
        if (action === 'Extract Office 365 Data ...' && this.selectedNodeData instanceof SearchResult &&
            !(this.selectedNodeData as SearchResult).name.toLowerCase().includes('modern attachment retrieve warning') &&
            !(this.selectedNodeData as SearchResult).name.toLowerCase().includes('missing channel name warning')
        ) {
            return false;
        }
        return true;
    }

    openDiscardPileLog(): void {
        this.commonService.openModal(DiscardPileLogComponent, {
            modalData: {},
            class: 'horizontal-percent-100 vertical-percent-100'
        })
    }

    openDocumentViewer(item: DrDocument, type: DocumentListTabType, external: boolean) {
        this.commonService.docListTabBeforeViewer = this.router.url.split(':')[1]?.split(')')[0];
        const documentViewerContext = new DocumentViewerContext();
        documentViewerContext.selectedDocument = lo_.cloneDeep(item);
        documentViewerContext.isGeneratedComGridsDocList = this.isGeneratedComGridsDocList;
        if (type === DocumentListTabType.family) {
            documentViewerContext.documentListTab = DocumentListTabType.family;
        } else if (type === DocumentListTabType.thread) {
            documentViewerContext.documentListTab = DocumentListTabType.thread;
            documentViewerContext.isShortMessageThreads = item.filetype === 'smf' || this.isShortMessageThreads;
        } else {
            documentViewerContext.documentListTab = DocumentListTabType.docList;
        }
        if (this.genericGrid.requestInfo && this.genericGrid.requestInfo.data) {
            documentViewerContext.startIndex = this.genericGrid.requestInfo.data.startIndex;
            documentViewerContext.hideDuplicates = this.genericGrid.requestInfo.data.suppressDuplicates;
            documentViewerContext.sortBy = this.genericGrid.requestInfo.data.sortBy;
            documentViewerContext.sortAscending = this.genericGrid.requestInfo.data.sortAscending;
        }

        this.commonService.docViewerOpened = true;
        if (external) {
            this.commonService.openExternalDocumentViewer(documentViewerContext);
        } else {
            if (this.currentEntityType === EntityType.clusterView && this.requestInfo?.data?.viewHandle) {
                this.commonService.expandedClusterView = {
                    clusterID: this.commonService.currentClusterView.data.clusterID,
                    isClusterExpanded: this.commonService.currentClusterView.data.isExpanded
                };
            }
            this.commonService.documentViewerOpenContext = documentViewerContext;
            this.router.navigate(['document-viewer'], { skipLocationChange: true });
        }
    }

    loadCurrentColumns(): void {
        if (this.genericGrid && !this.genericGrid.gridApi?.isDestroyed()) {
            if (this.genericGrid.gridApi?.getColumns()?.length > 0) {
                this.currentColumns = this.genericGrid.gridApi.getColumns().filter(c => c.isVisible()
                    && !isUndefined(c.getColDef().field) && c.getColDef().field !== 'tagList').map(c => c.getColDef().field);
            }
            if (this.currentColumns) {
                const index = this.currentColumns.indexOf('displayName');
                if (index !== -1) {
                    this.currentColumns[index] = 'filename';
                }
                this.commonService.sendMessage(MessageType.currentColumns, this.currentColumns);
            }
        }
    }

    onColumnVisibilityChanged(): void {
        this.loadCurrentColumns();
    }

    createManifest(node: GenericTreeNode): void {
        this.commonService.openModal(CreateManifestModalComponent, { modalData: { entity: this.selectedNodeData, defaultColumns: this.currentColumns }, class: 'vertical-percent-100 horizontal-percent-100', backdrop: false, keyboard: false });
    }

    getExportVolumeState(node: GenericTreeNode): string {
        let state = '';
        if (this.selectedNode && this.selectedNode.data) {
            state = this.selectedNode.data.stateDisplay;
        }
        return state;
    }

    ngOnDestroy(): void {
        this.changeDetector.detach();
        if (this.entityChangedEvent) {
            this.entityChangedEvent.unsubscribe();
        }
    }

    generateMatrixDocList(): void {
        this.commonService.sendMessage(MessageType.generateDocList, true);
    }
    undoMatrixChanges(): void {
        this.commonService.sendMessage(MessageType.undoLastSelection, true);
    }
}
